/**
 * Client-side rate limiting utility for auth operations
 * Note: This provides client-side protection; server-side rate limiting
 * should also be implemented in Cloudflare Workers or middleware
 */

import { appConfig } from '@/config/environment';

interface RateLimitEntry {
  attempts: number;
  firstAttempt: number;
  lockedUntil: number | null;
}

interface RateLimitConfig {
  maxAttempts: number;
  windowMs: number;
  lockoutMs: number;
}

const DEFAULT_CONFIG: RateLimitConfig = {
  maxAttempts: appConfig.SECURITY?.MAX_LOGIN_ATTEMPTS || 5,
  windowMs: 15 * 60 * 1000, // 15 minutes
  lockoutMs: 30 * 60 * 1000, // 30 minutes lockout
};

const STORAGE_KEY = 'auth_rate_limit';

class RateLimiter {
  private config: RateLimitConfig;

  constructor(config: Partial<RateLimitConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  private getEntry(key: string): RateLimitEntry | null {
    try {
      const stored = localStorage.getItem(`${STORAGE_KEY}_${key}`);
      if (!stored) return null;
      return JSON.parse(stored);
    } catch {
      return null;
    }
  }

  private setEntry(key: string, entry: RateLimitEntry): void {
    try {
      localStorage.setItem(`${STORAGE_KEY}_${key}`, JSON.stringify(entry));
    } catch (err) {
      console.warn('Failed to store rate limit entry:', err);
    }
  }

  private clearEntry(key: string): void {
    try {
      localStorage.removeItem(`${STORAGE_KEY}_${key}`);
    } catch {
      // Ignore storage errors
    }
  }

  /**
   * Check if an action is rate limited
   * @param key Unique identifier for the rate limit (e.g., email address)
   * @returns Object with allowed status and remaining info
   */
  check(key: string): {
    allowed: boolean;
    remainingAttempts: number;
    lockedUntil: Date | null;
    retryAfterMs: number | null;
  } {
    const entry = this.getEntry(key);
    const now = Date.now();

    // No previous attempts
    if (!entry) {
      return {
        allowed: true,
        remainingAttempts: this.config.maxAttempts,
        lockedUntil: null,
        retryAfterMs: null,
      };
    }

    // Check if currently locked out
    if (entry.lockedUntil && now < entry.lockedUntil) {
      return {
        allowed: false,
        remainingAttempts: 0,
        lockedUntil: new Date(entry.lockedUntil),
        retryAfterMs: entry.lockedUntil - now,
      };
    }

    // Check if window has expired (reset attempts)
    if (now - entry.firstAttempt > this.config.windowMs) {
      this.clearEntry(key);
      return {
        allowed: true,
        remainingAttempts: this.config.maxAttempts,
        lockedUntil: null,
        retryAfterMs: null,
      };
    }

    // Check remaining attempts
    const remainingAttempts = this.config.maxAttempts - entry.attempts;
    return {
      allowed: remainingAttempts > 0,
      remainingAttempts: Math.max(0, remainingAttempts),
      lockedUntil: entry.lockedUntil ? new Date(entry.lockedUntil) : null,
      retryAfterMs: null,
    };
  }

  /**
   * Record a failed attempt
   * @param key Unique identifier for the rate limit
   * @returns Updated rate limit status
   */
  recordFailure(key: string): {
    locked: boolean;
    remainingAttempts: number;
    lockedUntil: Date | null;
  } {
    const now = Date.now();
    const entry = this.getEntry(key);

    let newEntry: RateLimitEntry;

    if (!entry || now - entry.firstAttempt > this.config.windowMs) {
      // Start new window
      newEntry = {
        attempts: 1,
        firstAttempt: now,
        lockedUntil: null,
      };
    } else {
      // Increment attempts
      newEntry = {
        ...entry,
        attempts: entry.attempts + 1,
      };

      // Check if should lock out
      if (newEntry.attempts >= this.config.maxAttempts) {
        newEntry.lockedUntil = now + this.config.lockoutMs;
      }
    }

    this.setEntry(key, newEntry);

    const remainingAttempts = this.config.maxAttempts - newEntry.attempts;
    return {
      locked: newEntry.lockedUntil !== null,
      remainingAttempts: Math.max(0, remainingAttempts),
      lockedUntil: newEntry.lockedUntil ? new Date(newEntry.lockedUntil) : null,
    };
  }

  /**
   * Record a successful attempt (clears rate limit)
   * @param key Unique identifier for the rate limit
   */
  recordSuccess(key: string): void {
    this.clearEntry(key);
  }

  /**
   * Manually reset rate limit for a key
   * @param key Unique identifier for the rate limit
   */
  reset(key: string): void {
    this.clearEntry(key);
  }
}

// Singleton instance for auth rate limiting
export const authRateLimiter = new RateLimiter();

// Hook for React components
export const useRateLimiter = () => {
  return authRateLimiter;
};

export default RateLimiter;
